import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import type { AssistantResponseDto, UpdateAssistantDto } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { AlertCircle, RefreshCw, Copy } from 'lucide-react';
import { useChannelConnections } from '@/hooks/useChannelConnections';
import { useToast } from '@/hooks/use-toast';

const assistantSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  description: z.string().optional(),
  channelConnectionId: z.string().optional(),
  coordinatorUrl: z.string().url('URL inválida').or(z.literal('')).optional(),
  coordinatorApiKey: z.string().optional(),
  coordinatorOverrideConfig: z.string().optional(),
  responseAssistantUrl: z.string().url('URL inválida').or(z.literal('')).optional(),
  responseAssistantApiKey: z.string().optional(),
  responseAssistantOverrideConfig: z.string().optional(),
  bufferEnabled: z.boolean().optional(),
  bufferTimeoutMs: z.number().min(0).optional(),
  messageSplitEnabled: z.boolean().optional(),
  messageSplitDelayMs: z.number().min(0).optional(),
  testAllowedSenders: z.string().optional(),
});

type AssistantFormData = z.infer<typeof assistantSchema>;

interface AssistantFormProps {
  assistant?: AssistantResponseDto;
  onSubmit: (data: UpdateAssistantDto) => void;
  onRegenerateWebhook?: () => void;
  isSubmitting: boolean;
  isRegenerating?: boolean;
  error?: string;
}

export function AssistantForm({
  assistant,
  onSubmit,
  onRegenerateWebhook,
  isSubmitting,
  isRegenerating = false,
  error,
}: AssistantFormProps) {
  const { toast } = useToast();
  const { data: channelsData, isLoading: isLoadingChannels } = useChannelConnections();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AssistantFormData>({
    resolver: zodResolver(assistantSchema),
    defaultValues: {
      name: assistant?.name || '',
      description: assistant?.description || '',
      channelConnectionId: assistant?.channelConnectionId || undefined,
      coordinatorUrl: assistant?.config?.agentFlow?.coordinator?.url || '',
      coordinatorApiKey: assistant?.config?.agentFlow?.coordinator?.apiKey || '',
      coordinatorOverrideConfig: JSON.stringify(
        assistant?.config?.agentFlow?.coordinator?.overrideConfig || {},
        null,
        2
      ),
      responseAssistantUrl: assistant?.config?.agentFlow?.responseAssistant?.url || '',
      responseAssistantApiKey: assistant?.config?.agentFlow?.responseAssistant?.apiKey || '',
      responseAssistantOverrideConfig: JSON.stringify(
        assistant?.config?.agentFlow?.responseAssistant?.overrideConfig || {},
        null,
        2
      ),
      bufferEnabled: assistant?.config?.buffer?.enabled ?? true,
      bufferTimeoutMs: assistant?.config?.buffer?.timeoutMs ?? 2000,
      messageSplitEnabled: assistant?.config?.messageSplit?.enabled ?? true,
      messageSplitDelayMs: assistant?.config?.messageSplit?.delayMs ?? 500,
      testAllowedSenders: assistant?.config?.test?.allowedSenders?.join('\n') || '',
    },
  });

  const bufferEnabled = watch('bufferEnabled');
  const messageSplitEnabled = watch('messageSplitEnabled');

  const handleFormSubmit = (data: AssistantFormData) => {
    // Parse JSON strings for overrideConfig
    let coordinatorOverride = {};
    let responseAssistantOverride = {};

    try {
      if (data.coordinatorOverrideConfig && data.coordinatorOverrideConfig.trim()) {
        coordinatorOverride = JSON.parse(data.coordinatorOverrideConfig);
      }
    } catch (e) {
      toast({
        title: 'Erro',
        description: 'JSON inválido em Coordinator Override Config',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (data.responseAssistantOverrideConfig && data.responseAssistantOverrideConfig.trim()) {
        responseAssistantOverride = JSON.parse(data.responseAssistantOverrideConfig);
      }
    } catch (e) {
      toast({
        title: 'Erro',
        description: 'JSON inválido em Response Assistant Override Config',
        variant: 'destructive',
      });
      return;
    }

    const config = {
      agentFlow: {
        coordinator: data.coordinatorUrl ? {
          type: 'flowise' as const,
          url: data.coordinatorUrl,
          apiKey: data.coordinatorApiKey || undefined,
          overrideConfig: coordinatorOverride,
        } : undefined,
        responseAssistant: data.responseAssistantUrl ? {
          type: 'flowise' as const,
          url: data.responseAssistantUrl,
          apiKey: data.responseAssistantApiKey || undefined,
          overrideConfig: responseAssistantOverride,
        } : undefined,
      },
      buffer: {
        enabled: data.bufferEnabled,
        timeoutMs: data.bufferTimeoutMs,
      },
      messageSplit: {
        enabled: data.messageSplitEnabled,
        delayMs: data.messageSplitDelayMs,
      },
      test: {
        allowedSenders: data.testAllowedSenders
          ? data.testAllowedSenders.split('\n').filter((s) => s.trim())
          : [],
      },
    };

    const updateDto: UpdateAssistantDto = {
      name: data.name,
      description: data.description,
      channelConnectionId: data.channelConnectionId,
      config,
    };
    onSubmit(updateDto);
  };

  const handleCopyWebhook = async () => {
    if (!assistant?.config?.webhook?.url) return;

    try {
      await navigator.clipboard.writeText(assistant.config.webhook.url);
      toast({
        title: 'Copiado',
        description: 'Webhook URL copiado para a área de transferência',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar o webhook',
        variant: 'destructive',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 sm:space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Accordion type="multiple" defaultValue={['basic']} className="w-full">
        {/* Basic Information Panel */}
        <AccordionItem value="basic">
          <AccordionTrigger className="text-base sm:text-lg font-medium">
            Informações Básicas
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input id="name" {...register('name')} placeholder="My IABlue Atendimento" />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Descrição do assistente"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="channelConnectionId">Canal</Label>
                <Select
                  onValueChange={(value) => setValue('channelConnectionId', value === 'none' ? undefined : value)}
                  defaultValue={assistant?.channelConnectionId || 'none'}
                  disabled={isLoadingChannels}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um canal (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum canal</SelectItem>
                    {channelsData?.connections?.map((channel) => (
                      <SelectItem key={channel.id} value={channel.id}>
                        {channel.name}
                      </SelectItem>
                    )) || []}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Selecione um canal para gerar o webhook automaticamente
                </p>
              </div>

              {assistant?.config?.webhook?.url && (
                <div className="space-y-2">
                  <Label>Webhook URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={assistant.config.webhook.url}
                      readOnly
                      className="flex-1 bg-muted font-mono text-xs sm:text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleCopyWebhook}
                      title="Copiar webhook URL"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    {onRegenerateWebhook && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={onRegenerateWebhook}
                        disabled={isRegenerating}
                        title="Regenerar webhook URL"
                      >
                        <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Use esta URL no seu canal para receber webhooks
                  </p>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Advanced Configuration Panel */}
        <AccordionItem value="advanced">
          <AccordionTrigger className="text-base sm:text-lg font-medium">
            Configurações Avançadas
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 pt-2">
              {/* Agent Flow Configuration */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Fluxos de Agente
                </h4>

                {/* Coordinator Flow */}
                <div className="space-y-3 border-l-2 border-primary/20 pl-4">
                  <h5 className="text-sm font-medium">Coordenador (Agente My IA Blue)</h5>

                  <div className="space-y-2">
                    <Label htmlFor="coordinatorUrl">URL do Fluxo</Label>
                    <Input
                      id="coordinatorUrl"
                      {...register('coordinatorUrl')}
                      placeholder="https://flowise.example.com/api/v1/prediction/..."
                      type="url"
                    />
                    {errors.coordinatorUrl && (
                      <p className="text-sm text-destructive">{errors.coordinatorUrl.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="coordinatorApiKey">API Key (opcional)</Label>
                    <Input
                      id="coordinatorApiKey"
                      {...register('coordinatorApiKey')}
                      placeholder="sk-..."
                      type="password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="coordinatorOverrideConfig">Override Config (JSON)</Label>
                    <Textarea
                      id="coordinatorOverrideConfig"
                      {...register('coordinatorOverrideConfig')}
                      placeholder='{"temperature": 0.7, "maxTokens": 2000}'
                      rows={4}
                      className="font-mono text-xs"
                    />
                    <p className="text-sm text-muted-foreground">
                      Configurações específicas do fluxo em formato JSON
                    </p>
                  </div>
                </div>

                {/* Response Assistant Flow */}
                <div className="space-y-3 border-l-2 border-secondary/20 pl-4">
                  <h5 className="text-sm font-medium">Assistente de Resposta (Conversor JSON)</h5>

                  <div className="space-y-2">
                    <Label htmlFor="responseAssistantUrl">URL do Fluxo</Label>
                    <Input
                      id="responseAssistantUrl"
                      {...register('responseAssistantUrl')}
                      placeholder="https://converter.example.com/format"
                      type="url"
                    />
                    {errors.responseAssistantUrl && (
                      <p className="text-sm text-destructive">{errors.responseAssistantUrl.message}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      Opcional: URL para converter a resposta da IA em JSON padronizado
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="responseAssistantApiKey">API Key (opcional)</Label>
                    <Input
                      id="responseAssistantApiKey"
                      {...register('responseAssistantApiKey')}
                      placeholder="sk-..."
                      type="password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="responseAssistantOverrideConfig">Override Config (JSON)</Label>
                    <Textarea
                      id="responseAssistantOverrideConfig"
                      {...register('responseAssistantOverrideConfig')}
                      placeholder='{"temperature": 0.3, "format": "json"}'
                      rows={4}
                      className="font-mono text-xs"
                    />
                    <p className="text-sm text-muted-foreground">
                      Configurações específicas do fluxo em formato JSON
                    </p>
                  </div>
                </div>
              </div>

              {/* Buffer Configuration */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Buffer de Mensagens
                </h4>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="bufferEnabled"
                    {...register('bufferEnabled')}
                    className="rounded"
                  />
                  <Label htmlFor="bufferEnabled">Habilitar buffer de mensagens</Label>
                </div>

                {bufferEnabled && (
                  <div className="space-y-2 ml-6">
                    <Label htmlFor="bufferTimeoutMs">Tempo de buffer (ms)</Label>
                    <Input
                      id="bufferTimeoutMs"
                      type="number"
                      {...register('bufferTimeoutMs', { valueAsNumber: true })}
                      placeholder="2000"
                    />
                    <p className="text-sm text-muted-foreground">
                      Tempo de espera para agrupar mensagens consecutivas
                    </p>
                  </div>
                )}
              </div>

              {/* Message Split Configuration */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Divisão de Mensagens
                </h4>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="messageSplitEnabled"
                    {...register('messageSplitEnabled')}
                    className="rounded"
                  />
                  <Label htmlFor="messageSplitEnabled">Habilitar divisão de mensagens</Label>
                </div>

                {messageSplitEnabled && (
                  <div className="space-y-2 ml-6">
                    <Label htmlFor="messageSplitDelayMs">Delay entre mensagens (ms)</Label>
                    <Input
                      id="messageSplitDelayMs"
                      type="number"
                      {...register('messageSplitDelayMs', { valueAsNumber: true })}
                      placeholder="500"
                    />
                    <p className="text-sm text-muted-foreground">
                      Intervalo entre o envio de mensagens consecutivas da IA
                    </p>
                  </div>
                )}
              </div>

              {/* Test Mode Configuration */}
              {assistant?.status === 'TEST' && (
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Modo de Teste
                  </h4>

                  <div className="space-y-2">
                    <Label htmlFor="testAllowedSenders">Remetentes Autorizados</Label>
                    <Textarea
                      id="testAllowedSenders"
                      {...register('testAllowedSenders')}
                      placeholder="5511999999999&#10;5511888888888"
                      rows={4}
                    />
                    <p className="text-sm text-muted-foreground">
                      Um número de telefone por linha (somente esses poderão interagir no modo teste)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? 'Salvando...' : 'Salvar Configurações'}
        </Button>
      </div>
    </form>
  );
}
